import React from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAppStore } from '@/store';
import { ThemeProvider } from '@/components/ThemeProvider';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { SecurityErrorBoundary } from '@/components/SecurityErrorBoundary';
import { ToastContainer } from '@/components/ui/ToastContainer';
import { ToastProvider } from '@/contexts/ToastContext';
import { PWAUpdatePrompt } from '@/components/PWAUpdatePrompt';


// Layout Components
import { AppShell } from '@/components/layout/AppShell';

// Authentication Pages
import { LoginPage } from '@/pages/auth/LoginPage';
import { RegisterPage } from '@/pages/auth/RegisterPage';
import GoogleRegistrationPage from '@/pages/auth/GoogleRegistrationPage';
import SplashPage from '@/pages/auth/SplashPage';

// Main Application Pages
import { DashboardPage } from '@/pages/DashboardPage';
import { DailyReportPage } from '@/pages/reports/DailyReportPage';
import { WeeklyReportPage } from '@/pages/reports/WeeklyReportPage';
import { WeeklyReportDetailPage } from '@/pages/reports/WeeklyReportDetailPage';
import { GeneralReportPage } from '@/pages/reports/GeneralReportPage';
import { ProfilePage } from '@/pages/ProfilePage';
import { ProfileDetailsPage } from '@/pages/ProfileDetailsPage';
import { SettingsPage } from '@/pages/SettingsPage';
import ResetPasswordPage from '@/pages/ResetPasswordPage';

import { PTAssessmentPage } from '@/pages/PTAssessmentPage';
import BillingPage from '@/pages/BillingPage';
import HelpCenterPage from '@/pages/HelpCenterPage';
import WorkplacePage from '@/pages/WorkplacePage';
import { CompanyPage } from '@/pages/CompanyPage';
import { PTMSPage } from '@/pages/PTMSPage';
import { AnnouncementsPage } from '@/pages/AnnouncementsPage';
import { Chatbot } from '@/pages/chatbot';

// Admin Pages
import AdminDashboardPage from '@/pages/admin/AdminDashboardPage';
import UserManagementPage from '@/pages/admin/UserManagementPage';
import AnalyticsPage from '@/pages/admin/AnalyticsPage';

import RecentActivityPage from '@/pages/admin/RecentActivityPage';
import BillingDashboardPage from '@/pages/admin/BillingDashboardPage';
import TransactionsPage from '@/pages/admin/TransactionsPage';
import EmailBroadcastPage from '@/pages/admin/EmailBroadcastPage';

// Protected Route Component (requires authentication)
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAppStore();
  const location = useLocation();

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return <>{children}</>;
};

// Staff Route Component (requires authentication + staff privileges)
const StaffRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, user, fetchProfile } = useAppStore();
  const location = useLocation();
  const requestedProfile = React.useRef(false);

  React.useEffect(() => {
    if (isAuthenticated && user && user.is_staff === undefined && !requestedProfile.current) {
      requestedProfile.current = true;
      fetchProfile().catch(() => {});
    }
  }, [isAuthenticated, user, fetchProfile]);

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Wait for the staff flag to resolve before deciding (avoids bouncing real staff)
  if (user && user.is_staff === undefined) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <LoadingSpinner message="Verifying access..." />
      </div>
    );
  }

  if (!user?.is_staff) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
};

// Public Route Component (redirects authenticated users appropriately)
const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAppStore();

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
};

const App: React.FC = () => {
  const { theme, loading, initializeAuth, setupAuthListener } = useAppStore();

  // Initialize authentication state on app startup
  React.useEffect(() => {
    initializeAuth();
  }, [initializeAuth]);

  // Setup global authentication listener
  React.useEffect(() => {
    const cleanup = setupAuthListener();
    return cleanup;
  }, [setupAuthListener]);

  // Add loaded class to body and html after initial render to prevent icon text from showing
  React.useEffect(() => {
    const timer = setTimeout(() => {
      document.body.classList.add('loaded');
      document.documentElement.classList.add('loaded');
    }, 100);
    
    return () => clearTimeout(timer);
  }, []);

  return (
    <ErrorBoundary>
      <SecurityErrorBoundary>
        <ThemeProvider theme={theme}>
          <ToastProvider>
          <div className={`theme-${theme} min-h-screen bg-gray-50`}>
            {/* Loading Overlay */}
            {loading.isLoading && (
              <LoadingSpinner 
                message={loading.message} 
                fullScreen={true}
                color="primary"
              />
            )}

            {/* Toast Notifications */}
            <ToastContainer />

            {/* PWA Components */}
            <PWAUpdatePrompt />

            {/* Main Application */}
          <Routes>
            {/* Public Routes */}
            <Route
              path="/splash"
              element={
                <PublicRoute>
                  <SplashPage />
                </PublicRoute>
              }
            />
            <Route
              path="/login"
              element={
                <PublicRoute>
                  <LoginPage />
                </PublicRoute>
              }
            />
            <Route
              path="/register"
              element={
                <PublicRoute>
                  <RegisterPage />
                </PublicRoute>
              }
            />
            <Route
              path="/google-registration"
              element={
                <PublicRoute>
                  <GoogleRegistrationPage />
                </PublicRoute>
              }
            />
            <Route
              path="/reset-password"
              element={<ResetPasswordPage />}
            />

            {/* Protected Routes */}
            <Route
              path="/"
              element={
                <PublicRoute>
                  <Navigate to="/splash" replace />
                </PublicRoute>
              }
            />

            <Route path="/dashboard" element={<ProtectedRoute><AppShell><DashboardPage /></AppShell></ProtectedRoute>} />
            <Route path="/daily-report" element={<ProtectedRoute><AppShell><DailyReportPage /></AppShell></ProtectedRoute>} />
            <Route path="/weekly-report" element={<ProtectedRoute><AppShell><WeeklyReportPage /></AppShell></ProtectedRoute>} />
            <Route path="/weekly-report/:weekNumber" element={<ProtectedRoute><AppShell><WeeklyReportDetailPage /></AppShell></ProtectedRoute>} />
            <Route path="/general-report" element={<ProtectedRoute><AppShell><GeneralReportPage /></AppShell></ProtectedRoute>} />
            <Route path="/profile" element={<ProtectedRoute><AppShell><ProfilePage /></AppShell></ProtectedRoute>} />
            <Route path="/profile/details" element={<ProtectedRoute><AppShell><ProfileDetailsPage /></AppShell></ProtectedRoute>} />
            <Route path="/pt-assessment" element={<ProtectedRoute><AppShell><PTAssessmentPage /></AppShell></ProtectedRoute>} />
            <Route path="/settings" element={<ProtectedRoute><AppShell><SettingsPage /></AppShell></ProtectedRoute>} />
            <Route path="/billing" element={<ProtectedRoute><AppShell><BillingPage /></AppShell></ProtectedRoute>} />
            <Route path="/help" element={<ProtectedRoute><AppShell><HelpCenterPage /></AppShell></ProtectedRoute>} />
            <Route path="/enhancement" element={<ProtectedRoute><AppShell><Chatbot /></AppShell></ProtectedRoute>} />
            <Route path="/chatbot" element={<ProtectedRoute><AppShell><Chatbot /></AppShell></ProtectedRoute>} />
            <Route path="/company" element={<ProtectedRoute><AppShell><CompanyPage /></AppShell></ProtectedRoute>} />
            <Route path="/ptms" element={<ProtectedRoute><AppShell><PTMSPage /></AppShell></ProtectedRoute>} />
            <Route path="/announcements" element={<ProtectedRoute><AppShell><AnnouncementsPage /></AppShell></ProtectedRoute>} />
            <Route path="/workplace" element={<ProtectedRoute><AppShell><WorkplacePage /></AppShell></ProtectedRoute>} />
            <Route path="/admin" element={<StaffRoute><AppShell><AdminDashboardPage /></AppShell></StaffRoute>} />
            <Route path="/admin/users" element={<StaffRoute><AppShell><UserManagementPage /></AppShell></StaffRoute>} />
            <Route path="/admin/analytics" element={<StaffRoute><AppShell><AnalyticsPage /></AppShell></StaffRoute>} />
            <Route path="/admin/activity" element={<StaffRoute><AppShell><RecentActivityPage /></AppShell></StaffRoute>} />
            <Route path="/admin/billing" element={<StaffRoute><AppShell><BillingDashboardPage /></AppShell></StaffRoute>} />
            <Route path="/admin/transactions" element={<StaffRoute><AppShell><TransactionsPage /></AppShell></StaffRoute>} />
            <Route path="/admin/email-broadcast" element={<StaffRoute><AppShell><EmailBroadcastPage /></AppShell></StaffRoute>} />

            {/* 404 Route */}
            <Route
              path="*"
              element={
                <div className="min-h-screen flex items-center justify-center">
                  <div className="text-center">
                    <h1 className="text-4xl font-bold text-gray-900 mb-4">404</h1>
                    <p className="text-gray-600 mb-8">Page not found</p>
                    <button
                      onClick={() => window.history.back()}
                      className="btn-primary"
                    >
                      Go Back
                    </button>
                  </div>
                </div>
              }
            />
          </Routes>
        </div>
        </ToastProvider>
      </ThemeProvider>
      </SecurityErrorBoundary>
    </ErrorBoundary>
  );
};

export default App; 